import { useField } from "formik";
import { useCallback, useContext } from "react";

import { Button, FormGroup, MenuItem, AnchorButton } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";

import { ConfigurationItem, useReference } from "../../hooks/useReference";
import { CustomFormikReferenceComponentProps } from "../form/CustomFormikComponents";
import { SnContext } from "../../utils/context";

export default function UserInputReference({
    name,
    setFieldValue,
    companyId,
    tableRefName,
    label,
    required
}: CustomFormikReferenceComponentProps) {
    const [data, loading, error] = useReference(companyId, tableRefName);
    const snContext = useContext(SnContext);
    const snUri = `https://${snContext}.service-now.com`;
    const [field] = useField(name);
    const renderReferences: ItemRenderer<ConfigurationItem> = useCallback(
        (reference, { handleClick, modifiers }) => {
            if (!modifiers.matchesPredicate) {
                return null;
            }
            return (
                <MenuItem
                    active={modifiers.active}
                    key={reference.sysId}
                    onClick={handleClick}
                    text={reference.name}
                    //!todo fix <a> descendant
                    labelElement={
                        <AnchorButton
                            intent="primary"
                            minimal
                            icon="link"
                            target="_blank"
                            href={`${snUri}/nav_to.do?uri=${tableRefName}.do?sys_id=${reference.sysId}`}
                            // prevent click on parent
                            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
                        />
                    }
                />
            );
        },
        [snUri, tableRefName]
    );
    return (
        <>
            <FormGroup
                inline={false}
                label={label}
                labelFor="text-input"
                labelInfo={required ? "*" : undefined}
                disabled={!!error || loading}
            >
                <ReferenceSelect
                    itemPredicate={filterReferences}
                    itemRenderer={renderReferences}
                    items={data ? data : []}
                    onItemSelect={c => {
                        setFieldValue(`${name}.name`, c.name);
                        setFieldValue(`${name}.value`, c.sysId);
                    }}
                    noResults={<MenuItem disabled={true} text="No results." />}
                    popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                    disabled={!!error || loading}
                >
                    <Button
                        loading={loading}
                        text={field.value.name !== "" ? field.value.name : "Select"}
                        rightIcon="caret-down"
                    />
                </ReferenceSelect>
            </FormGroup>
        </>
    );
}

const ReferenceSelect = Select.ofType<ConfigurationItem>();

const filterReferences: ItemPredicate<ConfigurationItem> = (query, reference) => {
    return reference.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
